import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Settings, Mail, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { conciliacionProveedoresApi } from '../../../lib/api';
import { fmtDate } from '../utils';

const DEFAULT_CONFIG = {
  plazo_dias_habiles_default: 3,
  email_compras_default: 'compras@chirinosteel.com',
  dias_faltantes_antiguos_default: 30,
  dias_gracia_bloqueo_oc: 5,
  emails_aviso_bloqueo_to: [],
  emails_aviso_bloqueo_cc: [],
};

export function ConfiguracionSection({ user }) {
  const isGerencia = ['admin', 'gerencia'].includes((user?.rol || '').toLowerCase());
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [emailStatus, setEmailStatus] = useState(null);
  const [avisosStatus, setAvisosStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [runningAvisos, setRunningAvisos] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cRes, sRes, aRes] = await Promise.all([
        conciliacionProveedoresApi.getConfig(),
        conciliacionProveedoresApi.emailIngestStatus().catch(() => null),
        conciliacionProveedoresApi.avisosStatus().catch(() => null),
      ]);
      if (cRes.data) setConfig((prev) => ({ ...prev, ...cRes.data }));
      if (sRes?.data) setEmailStatus(sRes.data);
      if (aRes?.data) setAvisosStatus(aRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await conciliacionProveedoresApi.setConfig(config);
      toast.success('Configuración guardada');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const runAvisosNow = async () => {
    setRunningAvisos(true);
    try {
      const res = await conciliacionProveedoresApi.avisosRunNow(true);
      const d = res.data || {};
      toast.success(
        `Avisos: ${d.proveedores_advertencia || 0} advertencia · ${d.proveedores_bloqueados || 0} bloqueados · ` +
        `${d.bloqueados_nuevos_hoy || 0} nuevos · email ${d.email_enviado ? '✓ enviado' : '✗ ' + (d.email_omitido_motivo || 'no enviado')}`,
        { duration: 6000 }
      );
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setRunningAvisos(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Settings className="w-4 h-4" /> Configuración</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium block mb-1">Plazo días hábiles (default)</label>
            <Input type="number" value={config.plazo_dias_habiles_default}
              onChange={(e) => setConfig({ ...config, plazo_dias_habiles_default: Number(e.target.value) })}
              disabled={!isGerencia} data-testid="input-plazo-dias-habiles" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Email Compras (default)</label>
            <Input value={config.email_compras_default}
              onChange={(e) => setConfig({ ...config, email_compras_default: e.target.value })}
              disabled={!isGerencia} data-testid="input-email-compras-default" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium block mb-1">🟠 Umbral Bloqueo OC — días corridos sin contabilizar (default 30)</label>
            <Input type="number" min={1} value={config.dias_faltantes_antiguos_default}
              onChange={(e) => setConfig({ ...config, dias_faltantes_antiguos_default: Number(e.target.value) })}
              disabled={!isGerencia} data-testid="input-dias-faltantes-antiguos" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Si una factura faltante tiene más de N días desde su emisión, se bloquea al proveedor para emitir nuevas OCs.
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium block mb-1">🟡 Gracia antes del bloqueo — días de aviso (default 5)</label>
            <Input type="number" min={0} value={config.dias_gracia_bloqueo_oc}
              onChange={(e) => setConfig({ ...config, dias_gracia_bloqueo_oc: Number(e.target.value) })}
              disabled={!isGerencia} data-testid="input-dias-gracia-bloqueo" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Entre umbral y umbral+gracia, el proveedor queda en ADVERTENCIA y se notifica por email diariamente.
              Al cumplirse umbral+gracia, pasa a BLOQUEADO.
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium block mb-1">📧 Emails "Para" (To) del aviso diario — uno por línea</label>
            <Textarea rows={3}
              value={(config.emails_aviso_bloqueo_to || []).join('\n')}
              onChange={(e) => setConfig({ ...config, emails_aviso_bloqueo_to: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
              disabled={!isGerencia} className="text-xs font-mono" data-testid="textarea-emails-aviso-to" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium block mb-1">📧 Emails "Copia" (CC) — opcional, uno por línea</label>
            <Textarea rows={2}
              value={(config.emails_aviso_bloqueo_cc || []).join('\n')}
              onChange={(e) => setConfig({ ...config, emails_aviso_bloqueo_cc: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
              disabled={!isGerencia} className="text-xs font-mono" data-testid="textarea-emails-aviso-cc" />
          </div>
        </div>
        {isGerencia && (
          <div className="flex justify-end">
            <Button size="sm" onClick={saveConfig} disabled={saving} data-testid="btn-guardar-config-conciliacion">
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
          </div>
        )}

        {/* Email Auto-Ingest — solo informativo (Iter 366: la corrida manual vive en la Bandeja) */}
        <div className="border-t pt-3">
          <div className="text-xs font-medium mb-2 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Auto-ingesta de Emails (concilia@)</div>
          {emailStatus && (
            <div className="text-[11px] text-muted-foreground space-y-1 mb-2" data-testid="email-ingest-status">
              <div>Buzón: <strong>{emailStatus.mailbox || 'concilia@chirinosteel.com'}</strong></div>
              <div>Cron: <strong>{emailStatus.schedule || 'cada 30 min'}</strong></div>
              {emailStatus.running && <div className="text-blue-700">⏳ Revisión en curso…</div>}
              {emailStatus.last_run && <div>Última corrida: <strong>{fmtDate(emailStatus.last_run)}</strong>{emailStatus.run_by ? ` (manual: ${emailStatus.run_by})` : ''}</div>}
              {emailStatus.last_result && (
                <div>Último resultado: ✓ {emailStatus.last_result.procesados ?? 0} · omit {emailStatus.last_result.omitidos ?? 0} · err {emailStatus.last_result.errores?.length ?? 0}</div>
              )}
              {!emailStatus.is_configured && <div className="text-amber-700">⚠ MS Graph no está configurado</div>}
              <div className="text-slate-500">Para leer la casilla ahora, usa <strong>“Revisar casilla concilia@”</strong> en la pestaña Bandeja.</div>
            </div>
          )}
        </div>

        {/* Avisos diarios */}
        <div className="border-t pt-3">
          <div className="text-xs font-medium mb-2 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-orange-600" />
            Avisos Diarios de Bloqueo CECP
          </div>
          {avisosStatus && (
            <div className="text-[11px] text-muted-foreground space-y-1 mb-2">
              <div>Cron: <strong>{avisosStatus.schedule || 'diario 08:00'}</strong></div>
              <div>Umbral: <strong>{avisosStatus.umbral_dias}d</strong> · Gracia: <strong>{avisosStatus.gracia_dias}d</strong> (bloqueo efectivo a los {(avisosStatus.umbral_dias || 0) + (avisosStatus.gracia_dias || 0)}d)</div>
              {avisosStatus.marcha_blanca?.active && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded">
                  <div className="font-medium text-amber-900">📣 Marcha Blanca activa</div>
                  <div className="text-amber-800 text-[11px]">
                    Inicio: {fmtDate(avisosStatus.marcha_blanca.started_at)} · Fin: {fmtDate(avisosStatus.marcha_blanca.ends_at)}
                    · <strong>{avisosStatus.marcha_blanca.days_remaining} día(s) restantes</strong>.
                    Durante este período NO se aplican auto-bloqueos OC.
                  </div>
                </div>
              )}
              {avisosStatus.last_run_at && <div>Última corrida: <strong>{fmtDate(avisosStatus.last_run_at)}</strong></div>}
              {avisosStatus.last_resumen && (
                <div>
                  Último resumen: advertencia <strong>{avisosStatus.last_resumen.proveedores_advertencia ?? 0}</strong> ·
                  bloqueados <strong>{avisosStatus.last_resumen.proveedores_bloqueados ?? 0}</strong>
                  {avisosStatus.last_resumen.proveedores_marcha_blanca > 0 && (
                    <span> · marcha blanca <strong>{avisosStatus.last_resumen.proveedores_marcha_blanca}</strong></span>
                  )}
                  {' '}· email <strong>{avisosStatus.last_resumen.email_enviado ? '✓ enviado' : '✗ omitido'}</strong>
                  {avisosStatus.last_resumen.email_omitido_motivo && (
                    <span className="text-amber-700"> ({avisosStatus.last_resumen.email_omitido_motivo})</span>
                  )}
                </div>
              )}
            </div>
          )}
          <Button size="sm" variant="outline" onClick={runAvisosNow} disabled={runningAvisos} data-testid="btn-run-avisos-now">
            <ShieldAlert className="w-3.5 h-3.5 mr-1" /> {runningAvisos ? 'Ejecutando…' : 'Ejecutar avisos ahora'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
